import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCommonKeywordsWithTags } from '../../../../services/monitor/keywords/KeywordsService';
import { KeywordResponse } from '../../../../models/monitor/KeywordResponse';
import { GET_COMMON_KEYWORDS } from '../../../query/keyword/keys';

export const useGetCommonKeywordsWithTags = (
  projectId: number | null,
  // When true (e.g. the project still has pending questions), keep polling every 60s until the
  // keyword list is actually populated. The backend computes these from responses slightly after a
  // freshly-collected project's questions flip to ACTIVE, so without this the list stays empty
  // (collapsing the Brands ranking + leaving SoV labels unresolved) until a manual page refresh.
  expectMore?: boolean
) => {
  const queryClient = useQueryClient();
  const queryEnabled = !!projectId;

  const {
    data: commonKeywords,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<KeywordResponse[]>({
    queryKey: [GET_COMMON_KEYWORDS, projectId],
    queryFn: () => getCommonKeywordsWithTags(projectId!),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    retry: 3,
    retryDelay: 1000,
    // Grace period: poll while more data is expected OR the list is still empty; stops once it lands.
    refetchInterval: (query) =>
      (expectMore || (query.state.data?.length ?? 0) === 0) ? 60000 : false,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [GET_COMMON_KEYWORDS, projectId],
      refetchType: 'active',
    });

  return {
    commonKeywords: queryEnabled ? commonKeywords ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate,
  };
};
